import { formatCentsBRL } from "@/utils/money";
import type {
  ArranjoFaturamento,
  OmieEmpresaChave,
  PatchPropostaItemBody,
  PropostaDetail,
  PropostaItem,
} from "@/types/finance";

export type Empresas = PropostaDetail["empresas"];
export type PatchDeFaturamento = Omit<PatchPropostaItemBody, "id">;

export interface EscolhaDeArranjo {
  empresa: OmieEmpresaChave;
  arranjo: ArranjoFaturamento;
}

const NOMES_CURTOS: Record<OmieEmpresaChave, string> = {
  corisco: "Corisco",
  faisca: "Faísca",
  gota_serena: "Gota Serena",
};

export const ARRANJOS: Array<{ valor: ArranjoFaturamento; titulo: string; descricao: (empresa: string) => string }> = [
  {
    valor: "repasse_liquido",
    titulo: "Recebe tudo e repassa o líquido",
    descricao: (empresa) => `A agência paga tudo à ${empresa}, que paga o profissional já sem a comissão. Uma OS.`,
  },
  {
    valor: "taxa_e_comissao",
    titulo: "Fatura só a taxa",
    descricao: () => "A agência paga o profissional direto. A comissão é cobrada do profissional à parte.",
  },
  {
    valor: "total_e_comissao",
    titulo: "Recebe tudo e repassa o valor cheio",
    descricao: (empresa) => `A agência paga tudo à ${empresa}, que repassa o valor cheio. A comissão é cobrada à parte.`,
  },
];

export function nomeDaEmpresa(chave: OmieEmpresaChave | undefined | null): string {
  return chave ? NOMES_CURTOS[chave] ?? chave : "Corisco";
}

export function tituloDoArranjo(arranjo: ArranjoFaturamento): string {
  return ARRANJOS.find((opcao) => opcao.valor === arranjo)?.titulo ?? arranjo;
}

/** null = combinação personalizada (feita nos campos detalhados). */
export function arranjoDoItem(item: PropostaItem): ArranjoFaturamento | null {
  if (item.modo_agencia === "total" && item.modo_freelancer === "reter") return "repasse_liquido";
  if (item.modo_freelancer !== "cobrar") return null;
  return item.modo_agencia === "taxa" ? "taxa_e_comissao" : "total_e_comissao";
}

export function escolhaDoItem(item: PropostaItem): EscolhaDeArranjo | null {
  const arranjo = arranjoDoItem(item);
  return arranjo ? { empresa: item.empresa_agencia?.chave ?? "corisco", arranjo } : null;
}

/** Same as company_arrangement in the API: Gota Serena repassa o líquido, as outras faturam só a taxa. */
export function arranjoDaEmpresa(chave: OmieEmpresaChave, empresas: Empresas): ArranjoFaturamento {
  const empresa = empresas.find((candidata) => candidata.chave === chave);
  return empresa?.modelo === "total_repasse" ? "repasse_liquido" : "taxa_e_comissao";
}

/** Same as arrangement_modes in the API. */
export function patchDoArranjo(escolha: EscolhaDeArranjo, empresas: Empresas): PatchDeFaturamento {
  const temFaisca = empresas.some((empresa) => empresa.chave === "faisca");
  return {
    empresa_agencia: escolha.empresa,
    modo_agencia: escolha.arranjo === "taxa_e_comissao" ? "taxa" : "total",
    empresa_freelancer: escolha.arranjo === "repasse_liquido" || !temFaisca ? escolha.empresa : "faisca",
    modo_freelancer: escolha.arranjo === "repasse_liquido" ? "reter" : "cobrar",
  };
}

export function mesmaEscolha(primeira: EscolhaDeArranjo | null, segunda: EscolhaDeArranjo | null): boolean {
  return !!primeira && !!segunda && primeira.empresa === segunda.empresa && primeira.arranjo === segunda.arranjo;
}

/** O que o profissional faz com a nota neste período, como o invite vai dizer. */
export function fraseDaNota(item: PropostaItem): string {
  const empresa = nomeDaEmpresa(item.empresa_agencia?.chave);
  const valor = item.valor_profissional_cents;
  const comissao = item.comissao_cents;
  if (arranjoDoItem(item) === "repasse_liquido") {
    const liquido = valor != null && comissao != null ? formatCentsBRL(valor - comissao) : "o valor líquido";
    return `O profissional emite NF de ${liquido} contra a ${empresa}, já sem a comissão, e recebe da ${empresa}.`;
  }
  const valorCheio = valor != null ? formatCentsBRL(valor) : "o valor do período";
  const quemCobra = nomeDaEmpresa(item.empresa_freelancer?.chave ?? "faisca");
  const repasse = comissao ? `repassa ${formatCentsBRL(comissao)} à ${quemCobra}` : "não paga comissão";
  return `O profissional emite NF de ${valorCheio} contra a agência e ${repasse}.`;
}
